import { AxiosError } from 'axios';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import menuService from 'features/MenuList/menuService';
import { MenuClassification } from 'features/MenuList/models/menu';
import moment from 'moment';
import { FormEvent, forwardRef, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt } from 'react-icons/fa';
import { DATE_FORMAT_YYYY_MM_DD, displayError, handleServiceError } from 'utils';
import { MENU_TYPE_SOURCE } from '../constants';
import { CounterPatternMobileOrderDisplayPayload } from '../models';

export const MENU_TYPE_DEFAULT = 1;

const CustomInputDatePicker = forwardRef((props: any, ref: any) => {
  // You can style this further
  return (
    <>
      <div style={{ position: 'relative' }}>
        <FaCalendarAlt style={{ position: 'absolute', top: '8px', left: '10px' }} onClick={props.onClick} />
        <input
          className="form-control text-center"
          onClick={props.onClick}
          style={{ paddingLeft: '32px' }}
          value={props.value}
          readOnly
          onChange={props.onChange}
          type="text"
        />
      </div>
    </>
  );
});

export interface CounterPatternSettingFormSearchProps {
  onSearch?: (formSearchValue: CounterPatternMobileOrderDisplayPayload) => void;
  onExportCSV?: (formSearchValue: CounterPatternMobileOrderDisplayPayload) => void;
}

function CounterPatternSettingFormSearch({ onSearch, onExportCSV }: CounterPatternSettingFormSearchProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const formSearchRef = useRef<HTMLFormElement>(null);

  // state
  const [formSearchValue, setFormSearchValue] = useState<CounterPatternMobileOrderDisplayPayload>({
    menuClassification: 0,
    menuType: MENU_TYPE_DEFAULT,
    menuName: '',
    menuCode: '',
    dataDate: new Date(),
    posCode: '',
  });
  const [menuClassificationSource, setMenuClassificationSource] = useState<MenuClassification[]>([]);

  function loadMenuClassificationSource() {
    (async () => {
      try {
        const response = await menuService.getMenuClassification();
        setMenuClassificationSource(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  // effect
  useEffect(() => {
    // load init data source
    loadMenuClassificationSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (formSearchRef.current) {
      const newFormSearchValue = saveFormSearch();
      newFormSearchValue && onSearch?.(newFormSearchValue);
    }
  }

  function handleExportCSV() {
    if (formSearchRef.current) {
      const newFormSearchValue = saveFormSearch();
      newFormSearchValue && onExportCSV?.(newFormSearchValue);
    }
  }

  function saveFormSearch() {
    if (formSearchRef.current) {
      const newFormSearchValue: CounterPatternMobileOrderDisplayPayload = {
        ...formSearchValue,
        menuClassification: parseInt(formSearchRef.current['menu_classification'].value.trim()),
        menuType: parseInt(formSearchRef.current['menu_type'].value.trim()),
        menuName: formSearchRef.current['menu_name'].value.trim(),
        menuCode: formSearchRef.current['menu_code'].value.trim(),
        posCode: formSearchRef.current['pos_code'].value.trim(),
      };
      setFormSearchValue(newFormSearchValue);
      return newFormSearchValue;
    }
  }

  return (
    <form ref={formSearchRef} className="mt-3 mb-3" onSubmit={handleSubmit}>
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{t('MenuBookListPage.search')}</Accordion.Header>
          <Accordion.Body onExited={saveFormSearch}>
            <Row>
              <Col sm={6} md={6}>
                <Row className="mb-2">
                  <Col sm={3} className="text-end">
                    <Form.Label htmlFor="txt-menu-type" className="col-form-label">
                      {t('CounterPatternSettingPage.menu_classification')}：
                    </Form.Label>
                  </Col>
                  <Col sm={9} className="d-flex align-items-center">
                    <Form.Select name="menu_classification" style={{ width: '200px' }}>
                      <option value="0">選択</option>
                      {menuClassificationSource.map(function (menuClassification) {
                        return (
                          <option key={menuClassification.id} value={menuClassification.id}>
                            {menuClassification.name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={3} className="text-end">
                    <Form.Label htmlFor="txt-menu-type" className="col-form-label">
                      {t('CounterPatternSettingPage.menu_type')}：
                    </Form.Label>
                  </Col>
                  <Col sm={9} className="d-flex align-items-center">
                    {MENU_TYPE_SOURCE.map((menu, index) => {
                      return (
                        <Form.Check
                          inline
                          type="radio"
                          name="menu_type"
                          key={index}
                          defaultChecked={menu.value === MENU_TYPE_DEFAULT}
                          defaultValue={menu.value}
                          id={`menu-type-${menu.value}`}
                          label={menu.label}
                        />
                      );
                    })}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={3} className="text-end">
                    <Form.Label htmlFor="txt-menu-name" className="col-form-label">
                      {t('CounterPatternSettingPage.menu_name')}：
                    </Form.Label>
                  </Col>
                  <Col sm={9}>
                    <Form.Control id="txt-menu-name" name="menu_name" style={{ maxWidth: '200px' }} />
                  </Col>
                </Row>
                <Row className="mb-0">
                  <Col sm={3} className="text-end">
                    <Form.Label htmlFor="txt-menu-code" className="col-form-label">
                      {t('CounterPatternSettingPage.menu_code')}：
                    </Form.Label>
                  </Col>
                  <Col sm={9}>
                    <Form.Control id="txt-menu-code" name="menu_code" style={{ maxWidth: '200px' }} />
                  </Col>
                </Row>
              </Col>
              <Col sm={6}>
                <fieldset className="border rounded-3 px-3">
                  <legend className="float-none small w-auto px-3 mb-0">
                    {t('CounterPatternSettingPage.related_pos')}
                  </legend>
                  <Row className="mb-1">
                    <Col sm={2} className="text-end"></Col>
                    <Col sm={10}>
                      <Form.Label className="col-form-label">
                        {t('CounterPatternSettingPage.related_pos_guide')}
                      </Form.Label>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={2} className="text-end">
                      <Form.Label className="col-form-label">{t('CounterPatternSettingPage.target_date')}：</Form.Label>
                    </Col>
                    <Col sm={10}>
                      <div style={{ maxWidth: '160px' }}>
                        <ReactDatePicker
                          locale="ja"
                          selected={formSearchValue.dataDate}
                          onChange={(date: Date) => {
                            setFormSearchValue({ ...formSearchValue, dataDate: date });
                          }}
                          customInput={<CustomInputDatePicker />}
                          dateFormat="yyyy/MM/dd"
                          name="data_date"
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col sm={2} className="text-end">
                      <Form.Label htmlFor="txt-pos-code" className="col-form-label">
                        {t('CounterPatternSettingPage.pos_code')}：
                      </Form.Label>
                    </Col>
                    <Col sm={10}>
                      <Form.Control id="txt-pos-code" name="pos_code" style={{ maxWidth: '160px' }} />
                    </Col>
                  </Row>
                </fieldset>
              </Col>
            </Row>
            <Row>
              <Col className="justify-content-end d-flex gap-2">
                <Button type="submit">{t('CounterPatternSettingPage.search')}</Button>
                <Button type="button" className="btn--dark-green" onClick={handleExportCSV}>
                  {t('CounterPatternSettingPage.export_csv')}
                </Button>
              </Col>
            </Row>
          </Accordion.Body>
          <div className="accordion-body accordion-summary">
            <Row>
              <Col sm={'auto'}>
                <Form.Label className="col-form-label">
                  {t('CounterPatternSettingPage.menu_classification')}：
                </Form.Label>
                <Form.Label className="col-form-label">
                  {formSearchValue.menuClassification === 0
                    ? '選択'
                    : menuClassificationSource.find((item) => item.id === formSearchValue.menuClassification)?.name ||
                      '-'}
                </Form.Label>
              </Col>
              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('CounterPatternSettingPage.menu_type')}：</Form.Label>
                <Form.Label className="col-form-label">
                  {MENU_TYPE_SOURCE.find((x) => x.value === formSearchValue.menuType)?.label || '-'}
                </Form.Label>
              </Col>

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('CounterPatternSettingPage.menu_name')}：</Form.Label>
                <Form.Label className="col-form-label">{formSearchValue.menuName || '-'}</Form.Label>
              </Col>

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('CounterPatternSettingPage.menu_code')}：</Form.Label>
                <Form.Label className="col-form-label">{formSearchValue.menuCode || '-'}</Form.Label>
              </Col>

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('CounterPatternSettingPage.target_date')}：</Form.Label>
                <Form.Label className="col-form-label">
                  {moment(formSearchValue.dataDate).format(DATE_FORMAT_YYYY_MM_DD)}
                </Form.Label>
              </Col>

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('CounterPatternSettingPage.pos_code')}：</Form.Label>
                <Form.Label className="col-form-label">{formSearchValue.posCode || '-'}</Form.Label>
              </Col>
            </Row>
          </div>
        </Accordion.Item>
      </Accordion>
    </form>
  );
}

export default CounterPatternSettingFormSearch;
